import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, Search, RefreshCw, TrendingUp, Users, Euro, AlertCircle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEUR = (n) => (Number(n || 0)).toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("de-DE") : "—";

export default function VertriebsCockpit() {
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [sortBy, setSortBy] = useState("umsatz"); // "umsatz" | "tn"
  const [reaktCutoff, setReaktCutoff] = useState(90);
  const [maxAge, setMaxAge] = useState(730); // 2 Jahre = default, kann auf 365 gesetzt werden
  const [search, setSearch] = useState("");
  const [topKunden, setTopKunden] = useState(null);
  const [reakt, setReakt] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tk, rk] = await Promise.all([
        axios.get(`${API}/pflegeeinrichtungen/top-kunden?jahr=${jahr}&limit=50&sort_by=${sortBy}`),
        axios.get(`${API}/pflegeeinrichtungen/reaktivierung-faellig?tage=${reaktCutoff}&max_alter_tage=${maxAge}&limit=100`),
      ]);
      setTopKunden(tk.data);
      setReakt(rk.data);
    } catch (e) {
      toast.error("Laden fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [jahr, sortBy, reaktCutoff, maxAge]);

  const handleRecompute = async () => {
    if (!window.confirm("Kunden-Klassifikation neu berechnen?\nDies liest alle TN + Rechnungen neu ein.")) return;
    try {
      const t = toast.loading("Berechne...");
      const res = await axios.post(`${API}/pflegeeinrichtungen/recompute-kunden-klassifikation`);
      toast.success(`${res.data.total_einrichtungen} Einrichtungen aktualisiert`, {
        id: t,
        description: `${res.data.aktive_kunden} Kunden | ${res.data.warme_kontakte} Warm | ${res.data.kalt_akquise} Kalt`,
      });
      await loadAll();
    } catch (e) {
      toast.error("Recompute fehlgeschlagen");
    }
  };

  const kpi = useMemo(() => {
    const tk = topKunden?.top_kunden || [];
    return {
      umsatzYtd: tk.reduce((s, k) => s + (k.umsatz_im_jahr || 0), 0),
      tnYtd: tk.reduce((s, k) => s + (k.tn_anzahl_im_jahr || 0), 0),
      aktiveKunden: tk.length,
      zurueckHolen: reakt?.total || 0,
    };
  }, [topKunden, reakt]);

  const filteredTop = useMemo(() => {
    const list = topKunden?.top_kunden || [];
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter((k) =>
      (k.einrichtungsname || "").toLowerCase().includes(s) ||
      (k.ort || "").toLowerCase().includes(s) ||
      (k.ansprechpartner_name || "").toLowerCase().includes(s)
    );
  }, [topKunden, search]);

  const filteredReakt = useMemo(() => {
    const list = reakt?.kandidaten || [];
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter((k) => (k.einrichtungsname || "").toLowerCase().includes(s));
  }, [reakt, search]);

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto" data-testid="vertriebs-cockpit">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Vertriebs-Cockpit</h1>
          <p className="text-sm text-stone-600 mt-1">Top-Kunden, Umsatz, Anrufliste — alles auf einen Blick</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(jahr)} onValueChange={(v) => setJahr(Number(v))}>
            <SelectTrigger className="w-[120px]" data-testid="filter-jahr"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2026, 2025, 2024, 2023].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRecompute} data-testid="btn-recompute">
            <RefreshCw size={16} className="mr-2" /> Neu berechnen
          </Button>
        </div>
      </div>

      {/* KPI Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-stone-500 uppercase font-semibold">
              <Euro size={14} /> Umsatz {jahr}
            </div>
            <div className="text-2xl font-bold text-stone-800 mt-1">{fmtEUR(kpi.umsatzYtd)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-stone-500 uppercase font-semibold">
              <Users size={14} /> Teilnehmer {jahr}
            </div>
            <div className="text-2xl font-bold text-stone-800 mt-1">{kpi.tnYtd}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-stone-500 uppercase font-semibold">
              <TrendingUp size={14} /> Aktive Kunden {jahr}
            </div>
            <div className="text-2xl font-bold text-stone-800 mt-1">{kpi.aktiveKunden}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-stone-500 uppercase font-semibold">
              <AlertCircle size={14} /> Zurückholen
            </div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{kpi.zurueckHolen}</div>
            <div className="text-[10px] text-stone-500">seit &gt; {reaktCutoff} Tagen inaktiv</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input
          placeholder="Suche Einrichtung / Ort / Ansprechpartner..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 max-w-md"
          data-testid="input-search"
        />
      </div>

      {/* 2-column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: Top-Kunden Table */}
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b flex items-center justify-between gap-2">
              <div>
                <h2 className="font-bold text-lg">Top Kunden {jahr}</h2>
                <p className="text-xs text-stone-500">Nach {sortBy === "umsatz" ? "Umsatz" : "TN-Anzahl"} • {topKunden?.total || 0} Kunden</p>
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]" data-testid="filter-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="umsatz">Nach Umsatz</SelectItem>
                  <SelectItem value="tn">Nach TN-Anzahl</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-stone-500">Lade...</div>
              ) : filteredTop.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  Keine Kunden für {jahr} — bitte „Neu berechnen" klicken
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b sticky top-0">
                    <tr className="text-[11px] text-stone-500 uppercase">
                      <th className="text-left px-3 py-2 w-8">#</th>
                      <th className="text-left px-3 py-2">Einrichtung</th>
                      <th className="text-right px-3 py-2">Umsatz</th>
                      <th className="text-right px-3 py-2">TN</th>
                      <th className="text-left px-3 py-2">Zuletzt</th>
                      <th className="text-center px-3 py-2">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTop.map((k, i) => (
                      <tr
                        key={k.id}
                        className="border-b hover:bg-emerald-50/40 cursor-pointer"
                        onClick={() => window.location.href = `/pflegeeinrichtungen?id=${k.id}`}
                        data-testid={`top-row-${i}`}
                      >
                        <td className="px-3 py-2 font-bold text-stone-500">{i + 1}</td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-stone-800 truncate max-w-[220px]">{k.einrichtungsname}</div>
                          <div className="text-[11px] text-stone-500 truncate">{k.ort} {k.ansprechpartner_name ? "· " + k.ansprechpartner_name : ""}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-700">{fmtEUR(k.umsatz_im_jahr)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-blue-700">{k.tn_anzahl_im_jahr}</td>
                        <td className="px-3 py-2 text-[11px] text-stone-600">{fmtDate(k.letzte_bestellung_am)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {k.ansprechpartner_telefon && (
                              <a href={`tel:${k.ansprechpartner_telefon}`} className="p-1 rounded hover:bg-blue-100 text-blue-700" title={k.ansprechpartner_telefon}><Phone size={13} /></a>
                            )}
                            {k.ansprechpartner_email && (
                              <a href={`mailto:${k.ansprechpartner_email}`} className="p-1 rounded hover:bg-stone-100 text-stone-700" title={k.ansprechpartner_email}><Mail size={13} /></a>
                            )}
                            <a href={`/pflegeeinrichtungen?id=${k.id}`} className="p-1 rounded hover:bg-stone-100" title="Öffnen"><ExternalLink size={13} /></a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>

        {/* RIGHT: Reaktivierungs-Anrufliste */}
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-0">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h2 className="font-bold text-lg">Anrufen (Reaktivierung)</h2>
                  <p className="text-xs text-stone-500">Kunden ohne Bestellung • {reakt?.total || 0} Kandidaten</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={String(reaktCutoff)} onValueChange={(v) => setReaktCutoff(Number(v))}>
                    <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="filter-cutoff"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">&gt; 30 Tage</SelectItem>
                      <SelectItem value="60">&gt; 60 Tage</SelectItem>
                      <SelectItem value="90">&gt; 90 Tage</SelectItem>
                      <SelectItem value="180">&gt; 180 Tage</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={String(maxAge)} onValueChange={(v) => setMaxAge(Number(v))}>
                    <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="filter-maxage"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="365">letztes Jahr</SelectItem>
                      <SelectItem value="730">letzte 2 Jahre</SelectItem>
                      <SelectItem value="1095">letzte 3 Jahre</SelectItem>
                      <SelectItem value="99999">Karteileichen zeigen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {reakt?.inaktive_alter_kunden_ausgeschlossen > 0 && (
                <div className="text-[11px] text-stone-500 mt-2 bg-stone-50 rounded px-2 py-1">
                  ℹ {reakt.inaktive_alter_kunden_ausgeschlossen} sehr alte Kunden (&gt; {maxAge / 365 | 0} J.) sind ausgeblendet — auf „Karteileichen zeigen" umschalten um sie zu sehen
                </div>
              )}
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-stone-500">Lade...</div>
              ) : filteredReakt.length === 0 ? (
                <div className="p-8 text-center text-emerald-700">✓ Keine überfälligen Kunden</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b sticky top-0">
                    <tr className="text-[11px] text-stone-500 uppercase">
                      <th className="text-left px-3 py-2">Einrichtung</th>
                      <th className="text-right px-3 py-2">Umsatz gesamt</th>
                      <th className="text-right px-3 py-2">Tage</th>
                      <th className="text-center px-3 py-2">Anrufen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReakt.map((k) => {
                      const tage = k.tage_seit_bestellung;
                      const urgent = tage && tage > 180;
                      return (
                        <tr
                          key={k.id}
                          className={`border-b hover:bg-amber-50 cursor-pointer ${urgent ? "bg-red-50/30" : ""}`}
                          onClick={() => window.location.href = `/pflegeeinrichtungen?id=${k.id}`}
                        >
                          <td className="px-3 py-2">
                            <div className="font-semibold text-stone-800 truncate max-w-[200px]">{k.einrichtungsname}</div>
                            <div className="text-[11px] text-stone-500 truncate">{k.ansprechpartner_name || k.ort}</div>
                          </td>
                          <td className="px-3 py-2 text-right text-stone-700">{fmtEUR(k.umsatz_gesamt)}</td>
                          <td className="px-3 py-2 text-right">
                            <Badge className={`border-0 text-xs ${urgent ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                              {tage != null ? tage + " T" : "?"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            {(k.ansprechpartner_telefon || k.handynummer_ansprechpartner) ? (
                              <a
                                href={`tel:${k.ansprechpartner_telefon || k.handynummer_ansprechpartner}`}
                                className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-1 rounded text-xs font-medium"
                              >
                                <Phone size={12} /> Anrufen
                              </a>
                            ) : (
                              <span className="text-[10px] text-stone-400">keine Nr.</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
